import { useEffect, useState } from 'react';
import Cookies from 'js-cookie';
import { pulseAPI } from './pulseAPI';
import { hasWebSession } from '../../services/session';

const listeners = new Set();
const details = new Map();
const results = new Map();
let socket, reconnect, idle, lastToken, authGeneration = 0, reconnectAttempt = 0;
const emit = message => { for (const listener of listeners) listener(message); };
export function resetPulseState() {
  authGeneration++; lastToken = null;
  clearTimeout(reconnect); clearTimeout(idle);
  const current = socket; socket = null; current?.close();
  details.clear(); results.clear();
  emit({ type: 'reset' });
  window.dispatchEvent(new Event('assetflow:pulse-cache-cleared'));
}
window.addEventListener('assetflow:auth-cleared', resetPulseState);
window.addEventListener('assetflow:auth-ready', connect);
window.addEventListener('storage', event => { if (event.key === 'auth_token') { resetPulseState(); if (event.newValue) connect(); } });
function connect() {
  clearTimeout(idle);
  if (socket && socket.readyState < 2) return;
  const token = Cookies.get('auth_token') || localStorage.getItem('auth_token');
  if ((!token && !hasWebSession()) || !listeners.size) return;
  if (lastToken !== token) { details.clear(); results.clear(); lastToken = token; }
  const url = new URL('/api/pulse/live', process.env.REACT_APP_BACKEND_URL || window.location.origin);
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  const current = new WebSocket(url);
  socket = current;
  current.onopen = () => { reconnectAttempt = 0; current.send(JSON.stringify(token ? { token } : { type: 'authenticate' })); };
  current.onmessage = event => {
    if (socket !== current) return;
    let message; try { message = JSON.parse(event.data); } catch { return; }
    if (message.type === 'command' && message.command?.commandId) {
      results.set(message.command.commandId, message.command);
      if (results.size > 100) results.delete(results.keys().next().value);
    }
    emit(message);
  };
  current.onclose = () => {
    if (socket !== current) return;
    socket = null; emit({ type: 'disconnected' });
    if (listeners.size) reconnect = setTimeout(connect, Math.min(30000, 1000 * 2 ** reconnectAttempt++) + Math.random() * 1000);
  };
  current.onerror = () => current.close();
}
export function subscribePulse(listener) {
  listeners.add(listener); connect();
  return () => {
    listeners.delete(listener);
    if (!listeners.size) idle = setTimeout(() => { clearTimeout(reconnect); socket?.close(); }, 500);
  };
}
export async function loadPulseDetail(id) {
  const generation = authGeneration;
  const data = (await pulseAPI.detail(id)).data;
  if (generation !== authGeneration) return;
  details.set(id, data);
  emit({ type: 'detail', assetId: id, data });
  return data;
}
export function usePulseDetail(id) {
  const [data, setData] = useState(() => details.get(id));
  const [error, setError] = useState('');
  useEffect(() => {
    let active = true, timer, fetching = false;
    const refresh = async () => {
      if (fetching) return;
      fetching = true;
      try { await loadPulseDetail(id); if (active) setError(''); }
      catch (err) { if (active) setError(err.response?.data?.detail || 'Could not update device information.'); }
      finally { fetching = false; }
    };
    setData(details.get(id));
    const off = subscribePulse(message => {
      if (!active) return;
      if (message.type === 'detail' && message.assetId === id) setData(message.data);
      else if (message.type === 'device' && message.assetId === id) {
        if (message.device) setData(previous => previous ? { ...previous, device: { ...previous.device, ...message.device } } : previous);
        if (message.refresh) { clearTimeout(timer); timer = setTimeout(refresh, 80); }
      } else if (['ready', 'reload', 'resync_required'].includes(message.type)) refresh();
      else if (message.type === 'reset') setData(undefined);
    });
    refresh();
    const fallback = setInterval(refresh, 30000);
    return () => { active = false; off(); clearTimeout(timer); clearInterval(fallback); };
  }, [id]);
  return { data, error, refresh: () => loadPulseDetail(id) };
}
export async function runPulseCommand(id, kind, parameters = {}, onProgress = () => {}) {
  const generation = authGeneration;
  const waiter = { wake: null };
  const wait = () => new Promise(resolve => { const timer = setTimeout(resolve, 1500); waiter.wake = () => { clearTimeout(timer); resolve(); }; });
  const off = subscribePulse(message => { if (message.type === 'command') waiter.wake?.(); });
  try {
    const command = (await pulseAPI.command(id, kind, parameters, true)).data;
    const deadline = Date.now() + (kind.startsWith('power.') || (kind.startsWith('accounts.') && kind !== 'accounts.list') ? 45000 : 185000);
    while (Date.now() < deadline) {
      if (generation !== authGeneration) throw new Error('Your session has ended.');
      const cached = results.get(command.commandId);
      const pendingStates = ['queued', 'delivered', 'running'];
      const result = cached && !pendingStates.includes(cached.state) ? cached : (await pulseAPI.result(command.commandId)).data;
      if (!pendingStates.includes(result.state)) {
        if (['outcome_unknown', 'indeterminate'].includes(result.state)) throw new Error('The laptop has not confirmed the outcome. Check Executed commands before retrying.');
        if (result.state !== 'succeeded') throw new Error(result.result?.message || `Action ${result.state}.`);
        return result.result;
      }
      if (['power.shutdown', 'power.restart'].includes(kind) && result.receivedAt) return { message: 'Power request received by laptop' };
      onProgress(result.receivedAt ? 'Running…' : 'Sending…');
      await wait();
    }
    throw new Error('No completion received. Check the computer before retrying.');
  } finally { off(); }
}
